import { useEffect, useRef } from "react";
import Spinner from "../ui/Spinner/Spinner";
import { useIndicativeRateStore } from "../../stores/useIndicativeRateStore";

interface Currency {
  name: string;
  abbr: string;
  symbol: string;
  flag: string;
  displayRate: number;
}

interface CurrencyRate {
  _id: string;
  fromCurrency: Currency;
  toCurrency: Currency;
  rate: number;
  minimumAmount: number;
}

type IndicativeRatesProps = {
  isSidebarExpanded: boolean;
};

const IndicativeRates: React.FC<IndicativeRatesProps> = ({ isSidebarExpanded }) => {
  const widthClass = isSidebarExpanded ? "w-[1010px]" : "w-[1210px]";
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { rates, loading, error } = useIndicativeRateStore();

  // useEffect(() => {
  //   const fetchRates = async () => {
  //     try {
  //       setLoading(true);
  //       const response = await request.get({
  //         url: "/utility/exchange-rates",
  //       });

  //       const rawRates = response.data as any[];

  //       const formattedRates = rawRates.map((rate) => ({
  //         ...rate,
  //         fromCurrency: {
  //           ...rate.fromCurrency,
  //           displayRate: Number(rate.fromCurrency.displayRate),
  //         },
  //         toCurrency: {
  //           ...rate.toCurrency,
  //           displayRate: Number(rate.toCurrency.displayRate),
  //         },
  //       }));

  //       setRates(formattedRates);
  //     } catch (err) {
  //       setError("Error fetching rates. Please try again later.");
  //       console.error("Error fetching rates:", err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchRates();
  // }, []);


  useEffect(() => {
    if (!containerRef.current || !contentRef.current || rates.length === 0)
      return;

    const container = containerRef.current;
    const content = contentRef.current;

    // Calculate total width needed
    const contentWidth = Array.from(content.children).reduce(
      (total, child) => total + child.getBoundingClientRect().width + 16, // 16px for gap
      0
    );

    // Set animation duration based on content width
    const duration = contentWidth / 50; // Adjust speed by changing divisor

    // Apply animation
    content.style.animation = `scroll ${duration}s linear infinite`;

    // Pause animation on hover
    const handleMouseEnter = () => {
      content.style.animationPlayState = "paused";
    };
    const handleMouseLeave = () => {
      content.style.animationPlayState = "running";
    };

    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [rates]);

const formatRateDisplay = (rate: CurrencyRate) => {
  if (rate.fromCurrency.displayRate === 1) {
    return `1 ${rate.fromCurrency.abbr} = ${rate.toCurrency.symbol}${rate.toCurrency.displayRate.toFixed(2)}`;
  } else if (rate.toCurrency.displayRate === 1) {
    return `1 ${rate.toCurrency.abbr} = ${rate.fromCurrency.symbol}${rate.fromCurrency.displayRate.toFixed(2)}`;
  }

  return `1 ${rate.fromCurrency.abbr} = ${rate.toCurrency.symbol}${rate.toCurrency.displayRate.toFixed(2)}`;
};


  if (loading) {
    return <div className="flex justify-center items-center h-4">
    <Spinner className="h-4 w-4 text-blue-500" />
  </div>;
  }

  if (error) {
    return <div className="text-center py-4 text-red-500">{error}</div>;
  }

  if (rates.length === 0) {
    return <div className="text-center py-4">No rates available</div>;
  }

  return (
    <div className={`bg-white dark:bg-gray-800 shadow-sm p-2 transition-all duration-300 ${widthClass}`}>
      <style>{`
  @keyframes scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`}</style>

      <div ref={containerRef} className="relative overflow-hidden">
        <div ref={contentRef} className="flex w-max gap-2">
          {rates.map((rate) => (
            <div
            key={rate._id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-blue-500 dark:hover:border-blue-600 transition-colors duration-200"
            style={{ minWidth: 200, height: 50, width: 200 }} // You can tweak these as needed
          >
            <div className="flex items-center gap-2 h-full">
              {/* Flags */}
              <div className="flex items-center -space-x-2">
                <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                  <img
                    src={rate.fromCurrency.flag}
                    className="h-full w-full object-cover"
                    alt={rate.fromCurrency.name}
                  />
                </div>
                <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                  <img
                    src={rate.toCurrency.flag}
                    className="h-full w-full object-cover"
                    alt={rate.toCurrency.name}
                  />
                </div>
              </div>
          
              {/* Exchange Rate */}
              <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                {formatRateDisplay(rate)}
              </div>
            </div>
          </div>
          
          ))}

          {/* Duplicate items for seamless looping */}
          {rates.map((rate) => (
             <div
             key={rate._id}
             className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-blue-500 dark:hover:border-blue-600 transition-colors duration-200"
             style={{ minWidth: 200, height: 50, width: 200 }} 
           >
             <div className="flex items-center gap-2 h-full">
               {/* Flags */}
               <div className="flex items-center -space-x-2">
                 <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                   <img
                     src={rate.fromCurrency.flag}
                     className="h-full w-full object-cover"
                     alt={rate.fromCurrency.name}
                   />
                 </div>
                 <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                   <img
                     src={rate.toCurrency.flag}
                     className="h-full w-full object-cover"
                     alt={rate.toCurrency.name}
                   />
                 </div>
               </div>
           
               {/* Exchange Rate */}
               <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                 {formatRateDisplay(rate)}
               </div>
             </div>
           </div>      
          ))}
        </div>
      </div>
    </div>
  );
}

export default IndicativeRates;